import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader } from './PageHeader';
import { unwrap, useApi } from '../lib/api';
import { isZombie } from '../lib/characters';
import { PLAYER_PAGE_SIZE, hiddenTestText, lastActiveLabel, showingText } from '../lib/playerList';

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1';

/** A TEST plate for a flagged test account (the reason on hover). */
export function TestBadge({ reason }: { reason?: string | null }) {
  return (
    <span
      title={reason ?? 'Test account'}
      className="ml-1 rounded bg-caption px-1 text-xs font-semibold"
      data-testid="test-badge"
    >
      TEST
    </span>
  );
}

/**
 * The Players list (admin tools, 2026-10-08): real survivors first (alive, then most recently active, then newest), test
 * accounts hidden unless "Show test accounts" is on, 50 a page with "Showing X of N", and a hint when a search matches hidden
 * test accounts.
 */
export function PlayerList() {
  const { client } = useApi();
  const [q, setQ] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [offset, setOffset] = useState(0);
  const players = useQuery({
    queryKey: ['admin-players', q, includeTest, offset],
    queryFn: async () =>
      unwrap(
        await client.GET('/admin/players', {
          params: { query: { q, take: PLAYER_PAGE_SIZE, offset, includeTest } },
        }),
      ),
  });
  const data = players.data;
  const items = data?.items ?? [];
  const hint = data ? hiddenTestText(data.testMatches, includeTest) : null;
  const showTests = () => {
    setIncludeTest(true);
    setOffset(0);
  };

  return (
    <>
      <PageHeader
        title="Players"
        description="Find a player by email or name. Living survivors first, then the most recently active. Test accounts are hidden unless you show them."
      />
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <input
          aria-label="Search players"
          placeholder="Email or name"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOffset(0);
          }}
          className="w-full max-w-md rounded border-2 border-ink px-3 py-2"
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeTest}
            onChange={(e) => {
              setIncludeTest(e.target.checked);
              setOffset(0);
            }}
          />
          Show test accounts
        </label>
      </div>
      {players.isError && <p className="mb-2 text-sm text-blam">{String(players.error)}</p>}
      <table className="w-full border-2 border-ink bg-white text-sm">
        <thead className="bg-ink text-newsprint">
          <tr>
            <th className={th}>Name</th>
            <th className={th}>Email</th>
            <th className={th}>Active character</th>
            <th className={th}>Last active</th>
            <th className={th}>Best</th>
            <th className={th}>Trust</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} className="border-t border-newsprint hover:bg-caption/20">
              <td className={td}>
                <Link
                  to="/players/$playerId"
                  params={{ playerId: p.id }}
                  className="font-semibold underline"
                >
                  {p.displayName}
                </Link>
                {p.isTestAccount && <TestBadge reason={p.testAccountReason} />}
              </td>
              <td className={`${td} font-mono`}>{p.email}</td>
              <td className={td}>
                {p.runStatus ? (
                  <>
                    {isZombie(p.runStatus) ? (
                      <span className="rounded bg-formaldehyde px-1 font-semibold">zombie</span>
                    ) : (
                      p.runStatus
                    )}
                    , day {p.daysSurvived}
                  </>
                ) : (
                  '-'
                )}
              </td>
              <td className={td} title={p.lastActiveAt ?? undefined}>
                {lastActiveLabel(p.lastActiveAt)}
              </td>
              <td className={td}>{p.bestDays} days</td>
              <td className={`${td} ${p.trustScore >= 5 ? 'font-bold text-blam' : ''}`}>
                {p.trustScore}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data && (
        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
          <span data-testid="showing">{showingText(data.offset, items.length, data.total)}</span>
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PLAYER_PAGE_SIZE))}
            className="rounded border-2 border-ink px-2 py-0.5 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={offset + items.length >= data.total}
            onClick={() => setOffset(offset + PLAYER_PAGE_SIZE)}
            className="rounded border-2 border-ink px-2 py-0.5 disabled:opacity-40"
          >
            Next
          </button>
          {hint && (
            <span className="text-bruise">
              {hint} -{' '}
              <button type="button" className="underline" onClick={showTests}>
                show them
              </button>
            </span>
          )}
        </div>
      )}
    </>
  );
}
